import { EyeOutlined } from "@ant-design/icons";
import { MIRAIC_NO_IMAGE_URL } from "@di-devs/constants";
import {
	Button,
	Col,
	DatePicker,
	Descriptions,
	Divider,
	Form,
	Image,
	Input,
	Modal,
	message,
	Row,
	Tag,
} from "antd";
import dayjs from "dayjs";
import type React from "react";
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import styled from "styled-components";
import type { MemberResponse } from "../../../../shared/types/memberCore.types";

const CustomDatePicker = styled(DatePicker)`
	.ant-picker-input > input {
		text-align: center;
	}
`;

export interface MemberRenewalModalProps {
	isVisible: boolean;
	onClose: () => void;
	currentMember: MemberResponse | null;
	onSuccess?: () => void;
	title?: string;
	onMemberRenew: (data: {
		memberId: number;
		activeUntil: string;
	}) => Promise<void>;
	isLoading?: boolean;
}

export const MemberRenewalModal: React.FC<MemberRenewalModalProps> = ({
	isVisible,
	onClose,
	currentMember,
	onSuccess,
	title = "会員更新",
	onMemberRenew,
	isLoading = false,
}) => {
	const [form] = Form.useForm();
	const [isRenewPickerOpen, setIsRenewPickerOpen] = useState(false);
	const [renewPickerValue, setRenewPickerValue] = useState<Date | null>(null);

	useEffect(() => {
		if (isVisible && currentMember?.memberId) {
			const defaultDate = currentMember?.activeUntil
				? dayjs(currentMember?.activeUntil, "YYYY-MM-DD")
						.add(1, "year")
						.subtract(1, "day")
						.toDate()
				: dayjs(currentMember?.memberSince, "YYYY-MM-DD")
						.add(1, "year")
						.subtract(1, "day")
						.toDate();

			setRenewPickerValue(defaultDate);

			form.setFieldsValue({
				memberId: currentMember?.memberId,
				activeUntil: isMobile
					? dayjs(defaultDate).format("YYYY-MM-DD")
					: dayjs(defaultDate),
			});
		}
	}, [currentMember, form, isVisible]);

	const handleMemberRenew = async (data: any) => {
		if (!currentMember?.memberId) {
			return;
		}
		try {
			const activeUntil = isMobile
				? data.activeUntil
				: dayjs(data.activeUntil).format("YYYY-MM-DD");

			await onMemberRenew({
				memberId: Number(currentMember?.memberId),
				activeUntil: activeUntil,
			});

			message.success("会員更新が完了しました");
			onClose();
			onSuccess?.();
			form.resetFields();
		} catch (_error: any) {
			message.error("システムエラーが発生しました");
		}
	};

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	return (
		<Modal
			title={title}
			open={isVisible}
			onCancel={handleCancel}
			footer={null}
			destroyOnClose
			centered
			styles={{
				body: {
					maxHeight: "80vh",
					overflowY: "auto",
					overflowX: "hidden",
				},
			}}
		>
			<Row>
				<Col span={24}>
					<div className="text-center mb-4">
						{currentMember?.picUrl ? (
							<Image
								preview={{
									mask: <EyeOutlined rev={undefined} />,
									src: currentMember.picUrl,
									maskClassName: "rounded-full",
								}}
								width={100}
								height={100}
								className="rounded-full"
								src={`${currentMember.picUrl}/large`}
								fallback={MIRAIC_NO_IMAGE_URL}
							/>
						) : (
							<Image
								src={MIRAIC_NO_IMAGE_URL}
								width={100}
								height={100}
								className="rounded-full"
								preview={{
									mask: <EyeOutlined rev={undefined} />,
									src: MIRAIC_NO_IMAGE_URL,
									maskClassName: "rounded-full",
								}}
							/>
						)}
					</div>
					<p className="text-center font-bold text-xl">
						{(currentMember as any)?.memberAttributeId1 ||
							currentMember?.displayName ||
							"ー"}
						様
					</p>
					<Divider>会員情報</Divider>
					<Descriptions column={1} layout="horizontal" bordered>
						<Descriptions.Item label="ID" className="text-center">
							{currentMember?.memberId || "ー"}
						</Descriptions.Item>
						<Descriptions.Item label="会員コード" className="text-center">
							{currentMember?.memberCode || "ー"}
						</Descriptions.Item>
						<Descriptions.Item label="会員登録日" className="text-center">
							{currentMember?.memberSince
								? dayjs(currentMember.memberSince).format("YYYY年M月D日")
								: "ー"}
						</Descriptions.Item>
						<Descriptions.Item label="来店回数" className="text-center">
							{currentMember?.visits && currentMember?.visits.length > 0
								? currentMember.visits.length
								: "ー"}
							回
						</Descriptions.Item>
						<Descriptions.Item label="最終来店日" className="text-center">
							{currentMember?.visits && currentMember?.visits.length > 0
								? dayjs(currentMember.lastVisit).format("YYYY年M月D日")
								: "ー"}
						</Descriptions.Item>
						<Descriptions.Item label="有効期限" className="text-center">
							{currentMember?.activeUntil ? (
								<Tag
									color={
										dayjs(currentMember.activeUntil).isAfter(dayjs(), "day")
											? "green"
											: "red"
									}
									className="mr-0"
								>
									{dayjs(currentMember.activeUntil).format("YYYY年M月D日")}
								</Tag>
							) : (
								"ー"
							)}
						</Descriptions.Item>
					</Descriptions>
				</Col>
				<Divider>有効期間更新</Divider>
				<Col span={24}>
					<Form
						name="memberRenewForm"
						form={form}
						onFinish={handleMemberRenew}
						layout="vertical"
						preserve={false}
						size="large"
					>
						<Row justify="center">
							<Col>
								<Form.Item name="memberId" hidden>
									<Input />
								</Form.Item>
							</Col>
							<Col>
								<Form.Item
									name="activeUntil"
									label="有効期限更新"
									rules={[
										{
											required: true,
											message: "必須です",
										},
									]}
								>
									{isMobile ? (
										<Input
											placeholder="例：有効期限を選択してください"
											readOnly
											className="cursor-pointer"
											onPressEnter={(e) => e.preventDefault()}
											onClick={() => {
												setIsRenewPickerOpen(true);
											}}
										/>
									) : (
										<CustomDatePicker className="w-full" />
									)}
								</Form.Item>
							</Col>
							<Col span={24} className="text-center">
								<Button
									type="primary"
									htmlType="submit"
									loading={isLoading}
									className="px-12"
								>
									更新
								</Button>
							</Col>
						</Row>
					</Form>
					{isMobile && (
						<DatePicker
							//@ts-expect-error
							isOpen={isRenewPickerOpen}
							value={renewPickerValue}
							theme="ios"
							confirmText="確定"
							cancelText="キャンセル"
							min={dayjs().toDate()}
							onSelect={(time: any) => {
								setIsRenewPickerOpen(false);

								form.setFieldsValue({
									activeUntil: dayjs(time).format("YYYY-MM-DD"),
								});

								setRenewPickerValue(time);
							}}
							onCancel={() => {
								setIsRenewPickerOpen(false);
							}}
						/>
					)}
				</Col>
			</Row>
		</Modal>
	);
};

export default MemberRenewalModal;
